import React, { useState, useMemo, useEffect } from 'react';
import { PatientMeeting, User, MeetingType } from '../../types/api';
import { ManageColumnsModal, ColumnConfig } from './ManageColumnsModal';
import { SaveViewModal } from './SaveViewModal';
import { UploadTranscriptModal } from './UploadTranscriptModal';
import { ViewSelector } from '../ui/ViewSelector';
import { CustomButton } from '../ui/CustomButton';
import { ContextMenu } from '../ui/ContextMenu';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { useListViews } from '../../hooks/useListViews';
import { POPUP_STYLES } from '../../constants/popupStyles';
import apiService from '../../services/api';
import { getMeetingTypeName } from '../../utils/meetingUtils';
import { NoShowModal } from './NoShowModal';
import { CancelMeetingModal } from './CancelMeetingModal';
import { TestPromptModal } from './TestPromptModal';
import { saveLastViewId, getLastViewId, clearLastViewId } from '../../utils/viewPersistence';

interface EditCommentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (comments: string) => Promise<void>;
    currentComments: string;
    isSaving: boolean;
}

interface ViewMeetingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    patientId: string | number;
    patientName?: string;
    user: User;
    token: string;
    users: User[];
    meetingTypes: MeetingType[];
}

export const ViewMeetingsModal: React.FC<ViewMeetingsModalProps> = ({
    isOpen,
    onClose,
    patientId,
    patientName,
    user,
    token,
    users,
    meetingTypes,
}) => {
    const [meetings, setMeetings] = useState<PatientMeeting[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; meetingId: number } | null>(null);
    const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
    const [pendingAction, setPendingAction] = useState<{ action: string; meetingId: number } | null>(null);

    // Realtime Transcript Popup state
    const [isRtTranscriptModalOpen, setIsRtTranscriptModalOpen] = useState(false);
    const [rtTranscriptContent, setRtTranscriptContent] = useState<string>("");
    const [rtTranscriptLoading, setRtTranscriptLoading] = useState(false);
    const [rtTranscriptError, setRtTranscriptError] = useState<string | null>(null);

    // Upload Transcript state
    const [isUploadTranscriptModalOpen, setIsUploadTranscriptModalOpen] = useState(false);
    const [isUploadingTranscript, setIsUploadingTranscript] = useState(false);
    const [uploadTranscriptMeetingId, setUploadTranscriptMeetingId] = useState<number | null>(null);

    // Notification state (shows at bottom right)
    const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'loading'; text: string } | null>(null);


    // Edit Comments state
    const [isEditCommentsModalOpen, setIsEditCommentsModalOpen] = useState(false);
    const [editCommentsMeetingId, setEditCommentsMeetingId] = useState<number | null>(null);
    const [editCommentsValue, setEditCommentsValue] = useState("");
    const [isSavingComments, setIsSavingComments] = useState(false);

    // No Show Modal state
    const [isNoShowModalOpen, setIsNoShowModalOpen] = useState(false);
    const [selectedMeetingForNoShow, setSelectedMeetingForNoShow] = useState<PatientMeeting | null>(null);

    // Cancel Meeting Modal state
    const [isCancelMeetingModalOpen, setIsCancelMeetingModalOpen] = useState(false);
    const [selectedMeetingForCancel, setSelectedMeetingForCancel] = useState<PatientMeeting | null>(null);

    // Test Prompt Modal state (prompt developers only)
    const [selectedMeetingForTestPrompt, setSelectedMeetingForTestPrompt] = useState<PatientMeeting | null>(null);

    // All available columns
    const ALL_COLUMNS: ColumnConfig[] = [
        { id: "id", label: "ID", visible: true },
        { id: "date", label: "Date", visible: true },
        { id: "created_at", label: "Created At", visible: false },
        { id: "meetingtype_id", label: "Meeting Type", visible: true },
        { id: "user_id", label: "Therapist", visible: true },
        { id: "status", label: "Status", visible: true },
        { id: "diagnosis", label: "Diagnosis", visible: true },
        { id: "comments", label: "Comments", visible: false },
        { id: "summary_ready", label: "Summary Ready", visible: true },
        { id: "summary_sent", label: "Summary Sent", visible: false },
        { id: "summary_approved", label: "Summary Approved", visible: false },
        { id: "need_followup_in_weeks", label: "Follow-up (weeks)", visible: true },
        { id: "clinic_assignment", label: "Clinic Assignment", visible: false },
        { id: "re_inatke", label: "Re-intake", visible: false },
        { id: "cancellation_time", label: "Cancellation Time", visible: false },
        { id: "reason_of_cancellation", label: "Cancelled By", visible: false },
        { id: "cancel_reason", label: "Cancel/No Show Reason", visible: false },
        { id: "cancel_reason_additional_text", label: "Cancel Reason Additional Text", visible: false },
        { id: "warning", label: "Warning", visible: false },
        { id: "team_url", label: "Teams Link", visible: false },
        { id: "meeting_url", label: "Meeting Link", visible: true },
        { id: "transcript", label: "Transcript", visible: false },
        { id: "realtime_transcript", label: "Realtime Transcript", visible: false },
        { id: "call_transcript", label: "Call Transcript", visible: false },
        { id: "src", label: "Source", visible: true },
        { id: "ad_hoc", label: "AdHoc", visible: true },
    ];

    // Default preferences
    const defaultPreferences = {
        columnOrder: ALL_COLUMNS.map(col => col.id),
        columnWidths: {},
        columnVisibility: ALL_COLUMNS.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>),
        sortColumn: "date",
        sortOrder: "desc" as const,
    };

    // Use the preferences hook
    const { preferences, updatePreferences, resetToDefaults } = useTablePreferences({
        storageKey: 'view-meetings-table-preferences',
        defaultPreferences,
    });

    // Extract values from preferences
    const [sortColumn, setSortColumn] = useState(preferences.sortColumn);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">(preferences.sortOrder);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(preferences.columnWidths);
    const [columnOrder, setColumnOrder] = useState<string[]>(preferences.columnOrder);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(preferences.columnVisibility);

    // Modal state
    const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);
    const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
    const [currentViewId, setCurrentViewId] = useState<number | null>(null);
    const [baseViewId, setBaseViewId] = useState<number | null>(null);

    // Column resizing state
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);
    const [startX, setStartX] = useState<number>(0);
    const [startWidth, setStartWidth] = useState<number>(0);

    // Check if user is admin
    const isAdmin = useMemo(() => {
        return user?.role === 'admin' || user?.role === 'Admin';
    }, [user?.role]);

    // List Views integration
    const {
        privateViews,
        publicViews,
        createListView,
        updateListView,
        deleteListView,
        isLoading: isLoadingViews,
    } = useListViews({
        userId: user?.id as number,
        listName: 'patient-meetings',
        token,
        enabled: isOpen && !!user?.id && !!token,
    });

    // Track if we've already loaded the initial view to prevent re-loading when view is cleared
    const hasLoadedInitialView = React.useRef(false);

    // Load saved view on mount
    useEffect(() => {
        // Only run after views are loaded
        if (isLoadingViews) {
            return;
        }

        // Only auto-load view once on initial mount
        if (hasLoadedInitialView.current) {
            return;
        }

        const savedViewId = getLastViewId('patient-meetings');
        if (savedViewId !== null) {
            const allViews = [...privateViews, ...publicViews];
            const savedView = allViews.find(v => v.id === savedViewId);
            if (savedView) {
                handleLoadView(savedView);
                hasLoadedInitialView.current = true;
            } else {
                // View was deleted, clear the saved ID
                clearLastViewId('patient-meetings');
            }
        } else {
            // No view to load, mark as loaded to prevent future auto-loads
            hasLoadedInitialView.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingViews, privateViews, publicViews]);

    // Create user name map from passed-in users
    const userNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        users.forEach((u: any) => {
            map[u.id] = u.name || u.email;
        });
        return map;
    }, [users]);

    const getUserName = (userId: number): string => {
        return userNameMap[userId] || "";
    };

    // Fetch patient meetings when modal opens
    useEffect(() => {
        const fetchData = async () => {
            if (!isOpen || !patientId) return;

            setIsLoading(true);
            setError(null);

            try {
                // Fetch patient meetings
                const meetingsData = await apiService.getPatientMeetings(token, patientId);
                setMeetings(meetingsData);
            } catch (err: any) {
                console.error("Failed to load meetings:", err);
                setError(err.message || "Failed to load meetings");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [isOpen, patientId, token]);

    // Sync preferences when state changes
    useEffect(() => {
        updatePreferences({
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
        });
    }, [columnOrder, columnWidths, columnVisibility, sortColumn, sortOrder, updatePreferences]);

    // Format date
    const formatDate = (timestamp: number | string | undefined | null) => {
        if (!timestamp || timestamp === 0) return "";

        let date: Date;
        if (typeof timestamp === 'string') {
            date = new Date(timestamp);
        } else {
            date = new Date(timestamp);
        }

        if (isNaN(date.getTime())) return "";

        // Format as dd/mm/yyyy hh:mm
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    // Sort meetings
    const sortedMeetings = useMemo(() => {
        let sorted = [...meetings];

        sorted = sorted.sort((a, b) => {
            const aValue = (a as any)[sortColumn];
            const bValue = (b as any)[sortColumn];

            if (aValue === bValue) return 0;
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            const comparison = aValue < bValue ? -1 : 1;
            return sortOrder === "asc" ? comparison : -comparison;
        });

        return sorted;
    }, [meetings, sortColumn, sortOrder]);

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("desc");
        }
    };

    // Column resize handlers
    const handleResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(column);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingColumn) return;

            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff);

            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            setResizingColumn(null);
        };

        if (resizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [resizingColumn, startX, startWidth]);

    const ResizableHeader = ({ column, label, nonSortable = false }: { column: string; label: string; nonSortable?: boolean }) => {
        const isSorted = sortColumn === column;
        const columnRef = React.useRef<HTMLTableCellElement>(null);
        const width = columnWidths[column];

        const handleHeaderClick = (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('cursor-col-resize') || target.closest('.cursor-col-resize')) {
                return;
            }
            if (!nonSortable) {
                handleSort(column);
            }
        };

        return (
            <th
                ref={columnRef}
                onClick={handleHeaderClick}
                className={`relative px-4 py-3 text-left text-xs font-medium text-[#5B7F8A] uppercase tracking-wider whitespace-nowrap ${!nonSortable ? 'hover:bg-[#A0CFC0] cursor-pointer' : ''} select-none`}
                style={width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : {}}
            >
                <div className="flex items-center gap-1">
                    {label}
                    {!nonSortable && isSorted && (
                        <span className="text-[#5B7F8A]">
                            {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                    )}
                </div>
                <div
                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#5B7F8A] bg-[#A0CFC0]"
                    onMouseDown={(e) => handleResizeStart(e, column, columnRef.current?.offsetWidth || 150)}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        );
    };

    // Modal handlers
    const handleManageColumns = () => {
        setIsManageColumnsOpen(true);
    };

    const handleSaveColumns = (columns: ColumnConfig[]) => {
        const newOrder = columns.map(col => col.id);
        const newVisibility = columns.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>);

        setColumnOrder(newOrder);
        setColumnVisibility(newVisibility);
    };

    const handleResetColumns = () => {
        setColumnOrder(defaultPreferences.columnOrder);
        setColumnWidths(defaultPreferences.columnWidths);
        setColumnVisibility(defaultPreferences.columnVisibility);
        setSortColumn(defaultPreferences.sortColumn);
        setSortOrder(defaultPreferences.sortOrder);

        // Clear saved view
        setCurrentViewId(null);
        setBaseViewId(null);
        clearLastViewId('patient-meetings');

        resetToDefaults();
    };

    const handleDownloadTranscript = async (transcriptId: string, type: string) => {
        try {
            setNotification({ type: 'loading', text: `Downloading ${type}...` });
            // Extract file extension from transcriptId (e.g., "88cbac10-b316-459a-a9b1-80ace50358cd.vtt" -> ".vtt")
            const extension = transcriptId.includes('.') ? transcriptId.substring(transcriptId.lastIndexOf('.')) : '.txt';
            const filename = `${type}${extension}`;
            await apiService.downloadFile(transcriptId, filename);
            setNotification({ type: 'success', text: `${type} downloaded successfully` });
            setTimeout(() => setNotification(null), 3000);
        } catch (error: any) {
            console.error(`Failed to download ${type}:`, error);
            setNotification({
                type: 'error',
                text: `Failed to download ${type}: ${error.message || 'Unknown error'}`
            });
            setTimeout(() => setNotification(null), 5000);
        }
    };

    const handleOpenRtTranscript = async (meetingId: number) => {
        try {
            setRtTranscriptLoading(true);
            setRtTranscriptError(null);
            setRtTranscriptContent("");
            const content = await apiService.getMeetingRtTranscript(token, meetingId);
            setRtTranscriptContent(content);
            setIsRtTranscriptModalOpen(true);
        } catch (error: any) {
            console.error("Failed to fetch realtime transcript:", error);
            setRtTranscriptError(error.message || "Failed to load realtime transcript");
        } finally {
            setRtTranscriptLoading(false);
        }
    };

    const handleCopyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setNotification({ type: 'success', text: `${label} copied to clipboard` });
            setTimeout(() => setNotification(null), 2000);
        }).catch((error) => {
            console.error('Failed to copy to clipboard:', error);
            setNotification({ type: 'error', text: `Failed to copy ${label}` });
            setTimeout(() => setNotification(null), 3000);
        });
    };

    const handleUploadTranscript = async (meetingId: number, filename: string, text: string) => {
        try {
            setIsUploadingTranscript(true);
            setNotification({ type: 'loading', text: 'Uploading transcript...' });
            await apiService.uploadTranscript({ token, meeting_id: meetingId, filename, text });
            setNotification({ type: 'success', text: 'Transcript uploaded successfully' });
            setIsUploadTranscriptModalOpen(false);
            // Refresh meetings to show updated transcript
            setTimeout(() => setNotification(null), 3000);
        } catch (error: any) {
            console.error('Failed to upload transcript:', error);
            setNotification({
                type: 'error',
                text: `Failed to upload transcript: ${error.message || 'Unknown error'}`
            });
            setTimeout(() => setNotification(null), 5000);
        } finally {
            setIsUploadingTranscript(false);
        }
    };

    const handleRunSummary = async (meetingId: number) => {
        try {
            setNotification({ type: 'loading', text: 'Running meeting summary...' });
            await apiService.runMeetingSummary(token, meetingId);
            setNotification({ type: 'success', text: 'Meeting summary executed successfully' });
            setTimeout(() => setNotification(null), 3000);
        } catch (error: any) {
            console.error('Failed to run meeting summary:', error);
            setNotification({
                type: 'error',
                text: `Failed to run summary: ${error.response?.data?.message || error.message || 'Unknown error'}`
            });
            setTimeout(() => setNotification(null), 5000);
        }
    };

    // Handle opening edit comments modal
    const handleOpenEditComments = (meeting: PatientMeeting) => {
        setEditCommentsMeetingId(meeting.id);
        setEditCommentsValue(meeting.comments || "");
        setIsEditCommentsModalOpen(true);
        setContextMenu(null);
    };

    // Handle saving comments
    const handleSaveComments = async (comments: string) => {
        if (!editCommentsMeetingId) return;

        const meeting = meetings.find(m => m.id === editCommentsMeetingId);
        if (!meeting) return;

        setIsSavingComments(true);
        setNotification({ type: 'loading', text: 'Saving comments...' });

        try {
            await apiService.updateCommentsMeeting({
                token,
                meeting_id: editCommentsMeetingId,
                comments: comments,
                source: "meetings",
                meetingtype_id: meeting.meetingtype_id
            });

            setNotification({ type: 'success', text: 'Comments saved successfully' });
            setTimeout(() => setNotification(null), 3000);

            // Refresh meetings list
            const meetingsData = await apiService.getPatientMeetings(token, patientId);
            setMeetings(meetingsData);

            setIsEditCommentsModalOpen(false);
            setEditCommentsMeetingId(null);
            setEditCommentsValue("");
        } catch (error: any) {
            console.error('Failed to save comments:', error);
            setNotification({
                type: 'error',
                text: `Failed to save comments: ${error.response?.data?.message || error.message || 'Unknown error'}`
            });
            setTimeout(() => setNotification(null), 5000);
        } finally {
            setIsSavingComments(false);
        }
    };

    // View management handlers
    const handleUpdateView = async () => {
        // Use baseViewId since currentViewId might be cleared after manual changes
        if (!baseViewId) return;

        try {
            await updateListView({
                id: baseViewId,
                data: {
                    columnOrder,
                    columnWidths,
                    columnVisibility,
                    sortColumn,
                    sortOrder,
                },
            });
            // After saving, restore currentViewId to baseViewId to reflect the saved state
            setCurrentViewId(baseViewId);
            saveLastViewId('patient-meetings', baseViewId);
            hasLoadedInitialView.current = true;
        } catch (error) {
            console.error('Failed to update view:', error);
        }
    };

    const handleSaveAsView = () => {
        setIsSaveViewModalOpen(true);
    };

    const handleSaveView = async (name: string, type: 'private' | 'public', description?: string) => {
        try {
            const newView = await createListView({
                name,
                owner_id: user?.id as number,
                type,
                list_name: 'patient-meetings',
                data: {
                    columnOrder,
                    columnWidths,
                    columnVisibility,
                    sortColumn,
                    sortOrder,
                    description,
                },
            });
            // Set both IDs to the newly created view
            setCurrentViewId(newView.id);
            setBaseViewId(newView.id);
            saveLastViewId('patient-meetings', newView.id);
            hasLoadedInitialView.current = true;

            setIsSaveViewModalOpen(false);
        } catch (error) {
            console.error('Failed to save view:', error);
        }
    };

    const handleLoadView = (view: any) => {
        // Merge in any columns added since this view was saved so they still appear in Manage Columns.
        const savedOrder = view.data.columnOrder || [];
        const missingColumns = defaultPreferences.columnOrder.filter((id: string) => !savedOrder.includes(id));
        setColumnOrder([...savedOrder, ...missingColumns]);
        setColumnWidths(view.data.columnWidths);
        setColumnVisibility({ ...defaultPreferences.columnVisibility, ...view.data.columnVisibility });
        setSortColumn(view.data.sortColumn);
        setSortOrder(view.data.sortOrder);
        setCurrentViewId(view.id);
        setBaseViewId(view.id); // Track the base view for save functionality
        saveLastViewId('patient-meetings', view.id);
    };

    const handleDeleteView = async (viewId: number) => {
        try {
            await deleteListView(viewId);
            if (currentViewId === viewId) {
                handleResetColumns();
            }
        } catch (error) {
            console.error('Failed to delete view:', error);
        }
    };

    // Handle context menu (right-click on meeting row)
    const handleMeetingContextMenu = (e: React.MouseEvent, meetingId: number) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedMeetingId(meetingId);
        setContextMenu({ x: e.clientX, y: e.clientY, meetingId });
    };



    // Execute confirmed action
    const executeAction = async () => {
        if (!pendingAction) return;

        const meeting = meetings.find(m => m.id === pendingAction.meetingId);
        if (!meeting) {
            setPendingAction(null);
            return;
        }

        let status: string;
        let actionText: string;
        let successMessage: string;

        if (pendingAction.action === "Make Active") {
            status = "active";
            actionText = "Making active";
            successMessage = "Meeting marked as active successfully";
        } else {
            setPendingAction(null);
            return;
        }

        setNotification({ type: 'loading', text: `${actionText}...` });
        setPendingAction(null);

        try {
            await apiService.updateMeetingStatus({
                token,
                meeting_id: meeting.id,
                status: status,
                source: "UI",
                meetingtype_id: meeting.meetingtype_id,
                reason_of_cancellation: ""
            });
            setNotification({ type: 'success', text: successMessage });

            // Refresh meetings list
            const meetingsData = await apiService.getPatientMeetings(token, patientId);
            setMeetings(meetingsData);

            // Clear success message after 3 seconds
            setTimeout(() => setNotification(null), 3000);
        } catch (error: any) {
            console.error(`Failed to ${pendingAction.action} meeting:`, error);
            const errorMsg = error.response?.data?.message || error.message || 'An error occurred';
            setNotification({ type: 'error', text: `Failed to ${pendingAction.action.toLowerCase()} meeting: ${errorMsg}` });

            // Clear error message after 5 seconds
            setTimeout(() => setNotification(null), 5000);
        }
    };

    // Close context menu when clicking outside
    useEffect(() => {
        if (contextMenu) {
            const handleClick = () => setContextMenu(null);
            document.addEventListener('click', handleClick);
            return () => document.removeEventListener('click', handleClick);
        }
    }, [contextMenu]);

    const getCellContent = (meeting: PatientMeeting, columnId: string) => {
        switch (columnId) {
            case "id":
                return meeting.id;
            case "date":
                return formatDate(meeting.date);
            case "created_at":
                return formatDate((meeting as any).created_at) || "-";
            case "meetingtype_id":
                return getMeetingTypeName(meeting, meetingTypes);
            case "user_id":
                return getUserName(meeting.user_id);
            case "status":
                return (
                    <span className={`px-2 py-1 text-xs rounded ${meeting.status === 'done' ? 'bg-green-100 text-green-700' :
                        meeting.status === 'active' ? 'bg-blue-100 text-blue-700' :
                            meeting.status === 'canceled' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                        }`}>
                        {meeting.status}
                    </span>
                );
            case "diagnosis":
                return meeting.diagnosis || "-";
            case "comments":
                return meeting.comments || "-";
            case "summary_ready":
                return meeting.summary_ready ? "✓" : "-";
            case "summary_sent":
                return meeting.summary_sent ? "✓" : "-";
            case "summary_approved":
                return meeting.summary_approved ? "✓" : "-";
            case "need_followup_in_weeks":
                return meeting.need_followup_in_weeks || "-";
            case "clinic_assignment":
                return meeting.clinic_assignment || "-";
            case "re_inatke":
                return meeting.re_inatke ? "Yes" : "No";
            case "cancellation_time":
                return meeting.cancellation_time ? formatDate(meeting.cancellation_time) : "-";
            case "reason_of_cancellation":
                return meeting.reason_of_cancellation || "-";
            case "cancel_reason":
                return meeting.cancel_reason || "-";
            case "cancel_reason_additional_text":
                return meeting.cancel_reason_additional_text || "-";
            case "warning":
                return meeting.warning || "-";
            case "team_url":
                return meeting.team_url ? (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopyToClipboard(meeting.team_url, "Teams Link");
                        }}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm font-medium"
                        title="Copy Teams link"
                    >
                        📋 Copy
                    </button>
                ) : "-";
            case "meeting_url":
                return meeting.meeting_url ? (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopyToClipboard(meeting.meeting_url, "Meeting Link");
                        }}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-sm font-medium"
                        title="Copy meeting link"
                    >
                        📋 Copy
                    </button>
                ) : "-";
            case "transcript": {
                const transcript = (meeting as any).transcript;
                if (!transcript) return "-";
                return (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadTranscript(transcript, "transcript");
                        }}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="Download transcript"
                    >
                        ⬇️
                    </button>
                );
            }
            case "realtime_transcript": {
                const realtimeTranscript = (meeting as any).realtime_transcript;
                if (!realtimeTranscript || realtimeTranscript === 0) return "-";
                return (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRtTranscript(meeting.id);
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer"
                        title="View realtime transcript"
                    >
                        {realtimeTranscript}
                    </button>
                );
            }
            case "call_transcript": {
                if (!meeting.has_call) return "-";
                return (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRtTranscript(meeting.id);
                        }}
                        className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                        title="View call transcript"
                    >
                        ✓
                    </button>
                );
            }
            case "src":
                if (!meeting.src) return "-";
                // Check if src starts with "retool-"
                if (meeting.src.startsWith("retool-")) {
                    const userId = parseInt(meeting.src.substring(7), 10);
                    if (!isNaN(userId)) {
                        return getUserName(userId) || meeting.src;
                    }
                }
                return meeting.src;
            case "ad_hoc":
                return meeting.ad_hoc ? "✓" : "-";
            default:
                return "-";
        }
    };

    if (!isOpen) return null;

    return (
        <div
            data-ai-modal="ViewMeetingsModal"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
        >
            <div
                className="bg-[#B8DDD0] rounded-3xl p-4 w-[95vw] max-w-7xl shadow-2xl relative max-h-[95vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-gray-700 hover:text-gray-900 text-2xl font-light"
                >
                    ✕
                </button>

                {/* Header with Toolbar */}
                <div className="flex items-center justify-between mb-4 pr-8">
                    <h2 className={POPUP_STYLES.title}>
                        Meetings - {patientName || `Patient ${patientId}`}
                    </h2>
                    <div className="flex flex-wrap gap-2 items-center">
                        <CustomButton
                            variant="secondary"
                            size="sm"
                            onClick={handleManageColumns}
                        >
                            Manage Columns
                        </CustomButton>
                        <ViewSelector
                            privateViews={privateViews}
                            publicViews={publicViews}
                            currentViewId={currentViewId}
                            baseViewId={baseViewId}
                            onSelectView={handleLoadView}
                            onSaveView={handleUpdateView}
                            onSaveAsView={handleSaveAsView}
                            onDeleteView={handleDeleteView}
                            isLoading={isLoadingViews}
                            isAdmin={isAdmin}
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto bg-white/90 rounded-2xl px-2 py-4">
                    {isLoading ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B7F8A] mx-auto"></div>
                            <p className="text-gray-600 mt-4">Loading meetings...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-red-600">
                            <p>Error: {error}</p>
                        </div>
                    ) : sortedMeetings.length === 0 ? (
                        <div className="text-center py-12 text-gray-600">
                            <p>No meetings found</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                                <thead className="bg-[#A0CFC0]">
                                    <tr>
                                        {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => {
                                            const config = ALL_COLUMNS.find(col => col.id === columnId);
                                            if (!config) return null;

                                            return (
                                                <ResizableHeader
                                                    key={columnId}
                                                    column={columnId}
                                                    label={config.label}
                                                    nonSortable={columnId === "team_url" || columnId === "meeting_url"}
                                                />
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {sortedMeetings.map((meeting) => (
                                        <tr
                                            key={meeting.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={(e) => handleMeetingContextMenu(e, meeting.id)}
                                            style={{
                                                backgroundColor: selectedMeetingId === meeting.id ? '#f0f9ff' : undefined
                                            }}
                                        >
                                            {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => (
                                                <td key={columnId} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                                    {getCellContent(meeting, columnId)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <div className="fixed inset-0 z-[1100]" style={{ pointerEvents: 'none' }}>
                    <div style={{ pointerEvents: 'auto' }}>
                        <ContextMenu
                            x={contextMenu.x}
                            y={contextMenu.y}
                            options={(() => {
                                const meeting = meetings.find(m => m.id === contextMenu.meetingId);
                                if (!meeting) return [];

                                const options = [];

                                // Add "No Show" option if meeting is not already marked as no-show
                                if (meeting.status !== "noshow") {
                                    options.push({
                                        label: 'No Show',
                                        icon: '⛔',
                                        onClick: () => {
                                            setSelectedMeetingForNoShow(meeting);
                                            setIsNoShowModalOpen(true);
                                            setContextMenu(null);
                                        }
                                    });
                                }

                                // Add "Cancel" option if meeting is not already canceled
                                if (meeting.status !== "canceled") {
                                    options.push({
                                        label: 'Cancel',
                                        icon: '🚫',
                                        onClick: () => {
                                            setSelectedMeetingForCancel(meeting);
                                            setIsCancelMeetingModalOpen(true);
                                            setContextMenu(null);
                                        }
                                    });
                                }

                                // Add "Make Active" option if meeting is not already active
                                if (meeting.status !== "active") {
                                    options.push({
                                        label: 'Make Active',
                                        icon: '✅',
                                        onClick: () => {
                                            setPendingAction({ action: "Make Active", meetingId: meeting.id });
                                        }
                                    });
                                }

                                // Add "Upload Transcript" option
                                options.push({
                                    label: 'Upload Transcript',
                                    icon: '📤',
                                    onClick: () => {
                                        setUploadTranscriptMeetingId(meeting.id);
                                        setIsUploadTranscriptModalOpen(true);
                                        setContextMenu(null);
                                    }
                                });

                                // Add "Run Summary" option
                                options.push({
                                    label: 'Run Summary',
                                    icon: '📋',
                                    onClick: () => {
                                        handleRunSummary(meeting.id);
                                        setContextMenu(null);
                                    }
                                });

                                // Add "Test Prompt" option (prompt developers only)
                                if (user.prompt_developer) {
                                    options.push({
                                        label: 'Test Prompt',
                                        icon: '🧪',
                                        onClick: () => {
                                            setSelectedMeetingForTestPrompt(meeting);
                                            setContextMenu(null);
                                        }
                                    });
                                }

                                // Add "Edit Comments" option
                                options.push({
                                    label: 'Edit Comments',
                                    icon: '💬',
                                    onClick: () => {
                                        handleOpenEditComments(meeting);
                                    }
                                });

                                return options;
                            })()}
                            onClose={() => setContextMenu(null)}
                        />
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {pendingAction && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200]"
                    onClick={() => setPendingAction(null)}
                >
                    <div
                        className="bg-[#B8DDD0] rounded-3xl p-6 shadow-2xl max-w-md"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className={`${POPUP_STYLES.title} mb-4`}>
                            Confirm {pendingAction.action}
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to {pendingAction.action.toLowerCase()} this meeting?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setPendingAction(null)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeAction}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage Columns Modal */}
            {isManageColumnsOpen && (
                <ManageColumnsModal
                    isOpen={isManageColumnsOpen}
                    onClose={() => setIsManageColumnsOpen(false)}
                    columns={ALL_COLUMNS.map(col => ({
                        ...col,
                        visible: columnVisibility[col.id]
                    }))}
                    onSave={handleSaveColumns}
                />
            )}

            {/* Save View Modal */}
            {isSaveViewModalOpen && (
                <SaveViewModal
                    isOpen={isSaveViewModalOpen}
                    onClose={() => setIsSaveViewModalOpen(false)}
                    onSave={handleSaveView}
                    currentConfig={{
                        columnOrder,
                        columnWidths,
                        columnVisibility,
                        sortColumn,
                        sortOrder,
                    }}
                    isAdmin={isAdmin}
                />
            )}

            {/* Realtime Transcript Modal */}
            {isRtTranscriptModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1300] p-4"
                    onClick={() => setIsRtTranscriptModalOpen(false)}
                >
                    <div
                        className="bg-white rounded-3xl p-8 w-full max-w-4xl shadow-2xl relative max-h-[85vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => setIsRtTranscriptModalOpen(false)}
                            className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 text-2xl font-light"
                        >
                            ✕
                        </button>

                        {/* Title */}
                        <h2 className={`${POPUP_STYLES.title} mb-4`}>Realtime Transcript</h2>

                        {/* Content */}
                        <div className="flex-1 overflow-auto bg-gray-50 rounded-2xl p-6 border border-gray-200" dir="rtl">
                            {rtTranscriptLoading ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                                        <p className="text-gray-600">Loading realtime transcript...</p>
                                    </div>
                                </div>
                            ) : rtTranscriptError ? (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700" dir="ltr">
                                    <p className="font-semibold">Error</p>
                                    <p>{rtTranscriptError}</p>
                                </div>
                            ) : rtTranscriptContent ? (
                                <div className="text-gray-800 whitespace-pre-wrap font-mono text-sm leading-relaxed text-right">
                                    {rtTranscriptContent}
                                </div>
                            ) : (
                                <div className="text-center text-gray-500 py-12">
                                    <p>No transcript content available</p>
                                </div>
                            )}
                        </div>

                        {/* Close Button at Bottom */}
                        <div className="flex justify-end mt-4 gap-3">
                            <button
                                onClick={() => setIsRtTranscriptModalOpen(false)}
                                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-medium transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Transcript Modal */}
            {isUploadTranscriptModalOpen && (
                <UploadTranscriptModal
                    isOpen={isUploadTranscriptModalOpen}
                    onClose={() => setIsUploadTranscriptModalOpen(false)}
                    meetings={meetings}
                    meetingTypes={meetingTypes}
                    onUpload={handleUploadTranscript}
                    isUploading={isUploadingTranscript}
                    preSelectedMeetingId={uploadTranscriptMeetingId || undefined}
                />
            )}


            {/* No Show Modal */}
            {selectedMeetingForNoShow && (
                <NoShowModal
                    isOpen={isNoShowModalOpen}
                    onClose={() => {
                        setIsNoShowModalOpen(false);
                        setSelectedMeetingForNoShow(null);
                    }}
                    meetingId={selectedMeetingForNoShow.id}
                    meetingTypeId={selectedMeetingForNoShow.meetingtype_id}
                    token={token}
                    onSuccess={async () => {
                        setNotification({ type: 'success', text: 'Meeting marked as no-show successfully' });
                        setTimeout(() => setNotification(null), 3000);
                        // Refresh meetings list
                        const meetingsData = await apiService.getPatientMeetings(token, patientId);
                        setMeetings(meetingsData);
                    }}
                />
            )}

            {/* Test Prompt Modal */}
            {selectedMeetingForTestPrompt && (
                <TestPromptModal
                    isOpen={true}
                    onClose={() => setSelectedMeetingForTestPrompt(null)}
                    meetingId={selectedMeetingForTestPrompt.id}
                    meetingTypeId={selectedMeetingForTestPrompt.meetingtype_id}
                    token={token}
                    userEmail={user.email}
                />
            )}

            {/* Cancel Meeting Modal */}
            {selectedMeetingForCancel && (
                <CancelMeetingModal
                    isOpen={isCancelMeetingModalOpen}
                    onClose={() => {
                        setIsCancelMeetingModalOpen(false);
                        setSelectedMeetingForCancel(null);
                    }}
                    meetingId={selectedMeetingForCancel.id}
                    meetingTypeId={selectedMeetingForCancel.meetingtype_id}
                    token={token}
                    onSuccess={async () => {
                        setNotification({ type: 'success', text: 'Meeting canceled successfully' });
                        setTimeout(() => setNotification(null), 3000);
                        // Refresh meetings list
                        const meetingsData = await apiService.getPatientMeetings(token, patientId);
                        setMeetings(meetingsData);
                    }}
                />
            )}

            {/* Edit Comments Modal */}
            <EditCommentsModal
                isOpen={isEditCommentsModalOpen}
                onClose={() => {
                    setIsEditCommentsModalOpen(false);
                    setEditCommentsMeetingId(null);
                    setEditCommentsValue("");
                }}
                onSave={handleSaveComments}
                currentComments={editCommentsValue}
                isSaving={isSavingComments}
            />

            {/* Unified Notification - Bottom Right */}
            {notification && (
                <div
                    className="fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-[1400]"
                    style={{
                        backgroundColor:
                            notification.type === 'error' ? '#fee2e2' :
                                notification.type === 'loading' ? '#dbeafe' :
                                    '#d1fae5',
                        color:
                            notification.type === 'error' ? '#991b1b' :
                                notification.type === 'loading' ? '#1e40af' :
                                    '#065f46',
                        border: `1px solid ${notification.type === 'error' ? '#fca5a5' :
                            notification.type === 'loading' ? '#93c5fd' :
                                '#6ee7b7'
                            }`
                    }}
                >
                    {notification.text}
                </div>
            )}
        </div>
    );
};

const EditCommentsModal: React.FC<EditCommentsModalProps> = ({
    isOpen,
    onClose,
    onSave,
    currentComments,
    isSaving
}) => {
    const [comments, setComments] = useState(currentComments);

    useEffect(() => {
        setComments(currentComments);
    }, [currentComments]);

    if (!isOpen) return null;

    const handleSave = async () => {
        await onSave(comments);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1300]">
            <div
                className="bg-[#B8DDD0] rounded-3xl p-6 shadow-2xl max-w-md w-full mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-6">
                    <h2 className={POPUP_STYLES.title}>Edit Comments</h2>
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="text-gray-600 hover:text-gray-800 text-2xl font-light disabled:opacity-50"
                    >
                        ✕
                    </button>
                </div>

                <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">
                        Comment
                    </label>
                    <input
                        type="text"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Enter value"
                        disabled={isSaving}
                        className={POPUP_STYLES.input}
                    />
                </div>

                <div className="flex gap-4 justify-center">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        Save
                    </button>
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};
